import { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import {
  Box,
  BoxProps,
  Container,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';

import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import mixinSx from '@watershed/style/mixinSx';
import MaybeTypography from '@watershed/ui-core/utils/MaybeTypography';

import useLocationHash from '../hooks/useLocationHash';
import scrollRefIntoView from '../utils/scrollRefIntoView';
import PageHeader, { PageHeaderProps } from './PageHeader';

const pageSectionSx: SxProps<Theme> = {
  '&:not(:last-child)': {
    paddingBottom: 2,
  },
};

export function PageSection({
  id,
  title,
  titleVariant,
  subtitle,
  actions,
  anchor,
  children,
  ...props
}: BoxProps & {
  id?: string;
  title?: ReactNode;
  titleVariant?: 'h2' | 'h3';
  subtitle?: ReactNode;
  actions?: ReactNode;
  anchor?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const hash = useLocationHash();

  useEffect(() => {
    if (anchor && hash === anchor) {
      scrollRefIntoView(ref);
    }
  }, [anchor, hash]);

  return (
    <Box
      component="section"
      sx={pageSectionSx}
      id={anchor}
      {...props}
      ref={ref}
    >
      {title && (
        <PageSectionHeader
          title={title}
          titleVariant={titleVariant}
          subtitle={subtitle}
          actions={actions}
        />
      )}
      {children}
    </Box>
  );
}

export function PageSectionHeader({
  title,
  titleVariant = 'h2',
  subtitle,
  actions,
  ...props
}: (
  | BoxProps
  | {
      title: ReactNode; // title is already a prop on BoxProps
    }
) & {
  titleVariant?: 'h2' | 'h3';
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Box
      component={actions ? 'header' : undefined}
      padding={2}
      display="flex"
      alignItems="center"
      {...props}
    >
      <Box flexGrow={1}>
        <MaybeTypography variant={titleVariant}>{title}</MaybeTypography>
        {subtitle && (
          <Typography variant="body2" style={{ maxWidth: '75%' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions}
    </Box>
  );
}

function FixedHeaderFullPage({
  header,
  children,
  sx,
  hideOverflow = false,
}: {
  header: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  hideOverflow?: boolean;
}) {
  return (
    <Container
      disableGutters={true}
      maxWidth={false}
      sx={mixinSx(
        {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100vh',
          height: '100%',
        },
        sx
      )}
    >
      {header}

      <Container
        disableGutters={true}
        maxWidth={false}
        sx={{
          overflow: hideOverflow ? 'hidden' : 'auto',
          height: '100%',
        }}
      >
        {children}
      </Container>
    </Container>
  );
}

export default function PageContainer({
  isFullPage = true,
  fullPageFixedHeader = false,
  maxWidth = 'md',
  component = 'article',
  headerSx,
  style,
  sx,
  bg = 'paper',
  heading,
  headingRightCol,
  headingRenderRightCol,
  isHeadingCompact,
  tabs,
  documentTitle,
  children,
  renderHeader,
  testId,
  hideOverflow = false,
}: {
  isFullPage?: boolean;
  fullPageFixedHeader?: boolean;
  hideOverflow?: boolean;
  maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false;
  component?: 'article' | 'div';
  style?: CSSProperties;
  sx?: SxProps<Theme>;
  bg?: 'sunken' | 'paper';
  headerSx?: SystemStyleObject<Theme>;
  heading?: PageHeaderProps['heading'];
  headingRightCol?: PageHeaderProps['rightCol'];
  headingRenderRightCol?: PageHeaderProps['renderRightCol'];
  isHeadingCompact?: PageHeaderProps['isCompact'];
  tabs?: PageHeaderProps['tabs'];
  documentTitle?: string;
  children: any;
  renderHeader?: (header: React.ReactNode) => React.ReactNode;
  testId?: TestIds;
}) {
  const baseHeader =
    heading || headingRightCol ? (
      <PageHeader
        heading={heading}
        rightCol={headingRightCol}
        renderRightCol={headingRenderRightCol}
        isCompact={isHeadingCompact}
        tabs={isFullPage ? tabs : undefined}
        documentTitle={documentTitle}
        sx={headerSx}
      />
    ) : null;
  const header = renderHeader ? renderHeader(baseHeader) : baseHeader;
  const theme = useTheme();

  return isFullPage ? (
    fullPageFixedHeader ? (
      <FixedHeaderFullPage header={header} sx={sx} hideOverflow={hideOverflow}>
        {children}
      </FixedHeaderFullPage>
    ) : (
      <>
        {header}
        {children}
      </>
    )
  ) : (
    <>
      {header}
      <Box
        sx={{
          minHeight: '100%',
          bgcolor: bg === 'sunken' ? theme.palette.grey05 : theme.palette.white,
        }}
        data-testid={testId}
      >
        <Container
          maxWidth={maxWidth}
          component={component}
          style={style}
          sx={sx}
        >
          {children}
        </Container>
      </Box>
    </>
  );
}
